import { defineStore } from 'pinia'
import { ref } from 'vue'

import type { UserBehaviorPanelKey } from '../models/userBehavior.types'

import type { ReportViewType } from '@/features/reports'

export const useAudienceUiStore = defineStore('audienceUi', () => {
  const selectedMetrics = ref<Record<string, string>>({})
  const funnelViewTypes = ref<Record<string, ReportViewType>>({})

  function selectionKey(workspaceId: number | string, panel: UserBehaviorPanelKey): string {
    return `${workspaceId}:${panel}`
  }

  /**
   * D6 (contract 24 §3.3 #6): v3 cached the funnel table's table/chart choice in `customizeStore`
   * under `` `${tab.id}-${tab.name}` `` — but the funnel tab object has no `id`, so every funnel
   * shared the one key `undefined-<name>`. The funnel's identity is `funnelMetadataId`.
   */
  function funnelViewKey(workspaceId: number | string, funnelMetadataId: number): string {
    return `${workspaceId}:${funnelMetadataId}`
  }

  function funnelViewTypeFor(
    workspaceId: number | string,
    funnelMetadataId: number,
  ): ReportViewType | null {
    return funnelViewTypes.value[funnelViewKey(workspaceId, funnelMetadataId)] ?? null
  }

  function setFunnelViewType(
    workspaceId: number | string,
    funnelMetadataId: number,
    view: ReportViewType,
  ): void {
    funnelViewTypes.value = {
      ...funnelViewTypes.value,
      [funnelViewKey(workspaceId, funnelMetadataId)]: view,
    }
  }

  function metricFor(workspaceId: number | string, panel: UserBehaviorPanelKey): string | null {
    return selectedMetrics.value[selectionKey(workspaceId, panel)] ?? null
  }

  function setMetric(
    workspaceId: number | string,
    panel: UserBehaviorPanelKey,
    metric: string,
  ): void {
    selectedMetrics.value = {
      ...selectedMetrics.value,
      [selectionKey(workspaceId, panel)]: metric,
    }
  }

  function resetState(): void {
    selectedMetrics.value = {}
    funnelViewTypes.value = {}
  }

  return {
    selectedMetrics,
    metricFor,
    setMetric,
    funnelViewTypes,
    funnelViewTypeFor,
    setFunnelViewType,
    resetState,
  }
})
